import { makeAutoObservable, computed } from 'mobx';
import logger from '../helpers/logger';
import { store } from './store';
import { LabeledItem } from '../typegen/labeled-item';
import { ReceiptPrintServerProps } from '../typegen/receipt-print-server-props';
import { ReceiptStaticKvp } from '../typegen/receipt-static-kvp';
import barcode from '../../assets/barcode.svg';
import bpkLogo from '../../assets/billpaykiosk-logo.png';
import { Views } from '../../components/receipt-digital-view/types';
import { ViewProps } from '../typegen/view-props';
import { validateEmail, validatePhone } from '../../components/receipt-digital-view/utils';
import { ReceiptType } from '../typegen/receipt-type';
import enumHelper from '../helpers/enums';
import { formatLocalizedDateTime } from '../helpers/datetime';
import { formatCurrencyDisplay } from '../helpers/formatCurrencyDisplay';
import { PaymentType } from '../typegen/payment-type';
import { getPPBehavior } from '../integrations';
import { Item } from '../typegen/item';
import ReceiptContentType from '../types/ReceiptContentType';

type Kvp = LabeledItem<string>;
interface TranslatedReceiptStaticKvp extends Omit<ReceiptStaticKvp, 'labelKey'> {
	label: string;
}

export interface ReceiptHeaderData {
	companyLogoPath: string;
	companyName: string;
	companyPhone: string;
	extraInfo?: TranslatedReceiptStaticKvp[];
}

export interface ReceiptFooterData {
	bpkLogoPath: string;
	barcodePath: string;
	extraInfo?: TranslatedReceiptStaticKvp[];
}

export interface CashBoxRemovalReceiptData {
	removalTime: Date;
	employeeName: string;
}

export interface ReceiptBodyData {
	datetime: Kvp;
	customerId: Kvp;
	invoiceNumber?: Kvp;
	sessionNumber: Kvp;
	paymentNumber?: Kvp;
	previousBalance?: Kvp;
	amountPaid: Kvp;
	fee: Kvp;
	total: Kvp;
	approximateAccountBalance?: Kvp;
	kioskNameOrId: Kvp;
	paymentType: Kvp; // Cash, Check, Card
}

export interface ReceiptBodyCashBoxData {
	datetime: Kvp;
	kioskNameOrId: Kvp;
	employeeName: Kvp;
	ones: number;
	twos: number;
	fives: number;
	tens: number;
	twenties: number;
	fifties: number;
	hundreds: number;
	totalBills: number;
	totalCash: string;
}

export interface ReceiptPrintProps {
	headerData?: ReceiptHeaderData;
	items: ReceiptBodyData[];
	footerData?: ReceiptFooterData;
}

export interface ReceiptData {
	headerData: ReceiptHeaderData;
	items: ReceiptBodyData[];
	cashBoxItems: ReceiptBodyCashBoxData[];
	footerData: ReceiptFooterData;
}

/** Editable values used by the manager Test Receipts tool to build a receipt without a live transaction. */
export interface CustomReceiptInput {
	paymentType: PaymentType;
	datetime: string;
	customerId: string;
	invoiceNumber: string;
	sessionNumber: string;
	amountPaid: number;
	fee: number;
	previousBalance: number;
	approximateAccountBalance: number;
	kioskNameOrId: string;
}

export default class ReceiptStore {
	private _receiptData: ReceiptData | null = null;
	private _printCalled = false;
	private _customInput: CustomReceiptInput | null = null;
	digitalReceiptCurrentView: Views = Views.Enter;
	digitalReceiptCurrentData: ViewProps | null = null;
	digitalReceiptInputValue = '';

	constructor() {
		makeAutoObservable(this, {
			isDigitalReceiptConfirmButtonDisabled: computed,
		});
	}

	get receiptData(): ReceiptData | null {
		return this._receiptData;
	}

	get customInput(): CustomReceiptInput | null {
		return this._customInput;
	}

	setCustomInput = (input: CustomReceiptInput): void => {
		this._customInput = input;
	};

	get isDigitalReceiptConfirmButtonDisabled() {
		if (!this.digitalReceiptInputValue) {
			return true;
		}
		let valid = false;
		if (store.accountStore.receiptType === ReceiptType.Email) valid = validateEmail(this.digitalReceiptInputValue);
		else if (store.accountStore.receiptType === ReceiptType.Text) valid = validatePhone(this.digitalReceiptInputValue);
		return !valid;
	}

	setDigitalReceiptCurrentView = (view: Views) => {
		this.digitalReceiptCurrentView = view;
	};

	setDigitalReceiptCurrentData = (data: ViewProps | null) => {
		this.digitalReceiptCurrentData = data;
	};

	setDigitalReceiptInputValue = (value: string) => {
		this.digitalReceiptInputValue = value;
	};

	generateReceiptData = (serverProps: ReceiptPrintServerProps): ReceiptData | null => {
		try {
			this._receiptData = this.mapTransactionToReceiptData(serverProps);
			logger.logGeneral.log({}, 'Receipt data generated successfully', this._receiptData);
			return this._receiptData;
		} catch (error) {
			const mappingError = error instanceof Error ? error : new Error('Failed to generate receipt data');
			logger.logGeneral.error({}, 'Failed to generate receipt data', mappingError);
			throw mappingError;
		}
	};

	clearReceiptData = (): void => {
		this._receiptData = null;
		logger.logGeneral.log({}, 'Receipt data cleared');
	};

	/**
	 * Builds receipt data from the editable custom input (Test Receipts tool) while
	 * still resolving header/footer/labels from the real kiosk server props.
	 */
	generateCustomReceiptData = (serverProps: ReceiptPrintServerProps): ReceiptData | null => {
		if (!this._customInput) {
			logger.logGeneral.error({}, 'No custom receipt input provided', 'custom input is null');
			return null;
		}

		const input = this._customInput;
		const { getString, ppType, companyPolicy } = store.configStore;
		const pp = getPPBehavior(ppType);
		const {
			logoPath,
			companyLabelValue,
			phoneLabelValue,
			headerStaticKvpList,
			dateTimeLabelKey,
			customerIdLabelKey,
			invoiceNumberLabelKey,
			sessionNumberLabelKey,
			paymentNumberLabelKey,
			amountPaidLabelKey,
			feeLabelKey,
			totalAmountLabelKey,
			approximateAccountBalanceLabelKey,
			showPreviousBalance,
			previousBalanceLabelKey,
			kioskNameLabelKey,
			paymentTypeLabelKey,
			footerStaticKvpList,
		} = serverProps;

		const headerData: ReceiptHeaderData = {
			companyLogoPath: store.uiStore.printedLogoPath || logoPath,
			companyName: companyLabelValue,
			companyPhone: phoneLabelValue,
			extraInfo: this.mapStaticKvpList(headerStaticKvpList, getString),
		};

		const bodyItem: ReceiptBodyData = {
			datetime: { label: getString(dateTimeLabelKey), value: input.datetime },
			customerId: { label: getString(customerIdLabelKey), value: input.customerId },
			sessionNumber: { label: getString(sessionNumberLabelKey), value: input.sessionNumber },
			amountPaid: { label: getString(amountPaidLabelKey), value: this.formatCurrency(input.amountPaid) },
			fee: { label: getString(feeLabelKey), value: this.formatCurrency(input.fee) },
			total: { label: getString(totalAmountLabelKey), value: this.formatCurrency(input.amountPaid + input.fee) },
			kioskNameOrId: { label: getString(kioskNameLabelKey), value: input.kioskNameOrId },
			paymentType: { label: getString(paymentTypeLabelKey), value: getString(enumHelper.getPaymentTypeLabelKey(input.paymentType)) },
		};

		// Resolve Invoice Number / Payment Number from the configured PP so the test receipt matches
		// what a real transaction would render. The single editable value feeds both the synthetic
		// invoice number and the kioskPaymentId; each PP surfaces only the row it cares about.
		const syntheticItem: Item = {
			name: '',
			itemNumber: input.invoiceNumber,
			obscuredItemNumber: input.invoiceNumber,
			amountDue: 0,
			amountPastDue: 0,
			paymentMethods: [],
			isOverPaymentAllowed: false,
			isPartialPaymentAllowed: false,
			isInactiveAccount: false,
		};
		const invoiceNumberValue = pp.getReceiptInvoiceNumber(syntheticItem, input.invoiceNumber, companyPolicy.obscuring.printItemNumber);
		if (invoiceNumberValue) {
			bodyItem.invoiceNumber = { label: getString(invoiceNumberLabelKey), value: invoiceNumberValue };
		}
		const paymentNumberValue = pp.getReceiptPaymentNumber?.(input.invoiceNumber);
		if (paymentNumberValue) {
			bodyItem.paymentNumber = { label: getString(paymentNumberLabelKey ?? 'payment-number'), value: paymentNumberValue };
		}

		if ((showPreviousBalance ?? false) && input.previousBalance !== 0) {
			bodyItem.previousBalance = {
				label: getString(previousBalanceLabelKey ?? 'previous-balance'),
				value: this.formatCurrency(input.previousBalance),
			};
		}

		bodyItem.approximateAccountBalance = {
			label: getString(approximateAccountBalanceLabelKey),
			value: this.formatCurrency(input.approximateAccountBalance),
		};

		const footerData: ReceiptFooterData = {
			bpkLogoPath: bpkLogo,
			barcodePath: barcode,
			extraInfo: this.mapStaticKvpList(footerStaticKvpList, getString),
		};

		this._receiptData = {
			headerData,
			items: [bodyItem],
			cashBoxItems: [],
			footerData,
		};
		return this._receiptData;
	};

	/** Builds the digital (text/email) receipt payload from the current custom input. */
	buildDigitalReceiptData = (serverProps: ReceiptPrintServerProps): ReceiptPrintProps | null => {
		const data = this.generateCustomReceiptData(serverProps);
		if (!data) return null;
		return { headerData: data.headerData, items: data.items, footerData: data.footerData };
	};

	print = (receiptContentType: ReceiptContentType, serverProps: ReceiptPrintServerProps, cashBoxRemovalData?: CashBoxRemovalReceiptData): void => {
		try {
			if (this._printCalled) {
				logger.logGeneral.warn({}, 'Print already called, ignoring duplicate call');
				return;
			}
			this._printCalled = true;

			// Set data based on content type
			switch (receiptContentType) {
				case ReceiptContentType.Standard:
				default:
					this.generateReceiptData(serverProps);
					break;
				case ReceiptContentType.Demo:
					this._receiptData = this.generateDemoReceiptData();
					logger.logGeneral.log({}, 'Demo receipt data generated for printing');
					break;
				case ReceiptContentType.DeviceTest:
					this._receiptData = this.generaterateDeviceTestReceiptData(serverProps);
					break;
				case ReceiptContentType.CashBoxTotals:
					this._receiptData = this.generateCashBoxTotalsReceiptData(serverProps, cashBoxRemovalData);
					break;
				case ReceiptContentType.Custom:
					this.generateCustomReceiptData(serverProps);
					break;
			}

			// Ensure we have receipt data
			if (!this._receiptData) {
				const error = new Error('No receipt data available for printing');
				logger.logGeneral.error({}, 'Print failed: No receipt data', error);
				return;
			}

			logger.logGeneral.log({}, 'Receipt print initiated', this._receiptData);

			// Mount the receipt in DOM
			if (store.uiStore) store.uiStore.setReceiptMounted(true);

			// Small delay to ensure DOM is rendered, then trigger print
			setTimeout(() => window.print(), 100);
		} catch (error) {
			const printError = error instanceof Error ? error : new Error('Unknown print error');
			logger.logGeneral.error({}, 'Receipt print failed', printError);
		}
	};

	receiptReceived = (): void => {
		// Unmount the receipt from DOM
		if (store.uiStore) store.uiStore.setReceiptMounted(false);

		// Clear the receipt data
		this.clearReceiptData();
		this._printCalled = false;
		logger.logGeneral.log({}, 'Receipt received - markup unmounted from DOM');
	};

	private mapTransactionToReceiptData = ({
		logoPath,
		companyLabelValue,
		phoneLabelValue,
		headerStaticKvpList,
		dateTimeLabelKey,
		customerIdLabelKey,
		invoiceNumberLabelKey,
		sessionNumberLabelKey,
		paymentNumberLabelKey,
		amountPaidLabelKey,
		feeLabelKey,
		totalAmountLabelKey,
		approximateAccountBalanceLabelKey,
		showPreviousBalance,
		previousBalanceLabelKey,
		kioskNameLabelKey,
		paymentTypeLabelKey,
		footerStaticKvpList,
	}: ReceiptPrintServerProps): ReceiptData | null => {
		const { getString, ppType, companyPolicy, currentLanguage } = store.configStore;
		const { cartItems, paymentType, transactionFee, cartTotalAmount, cartId, kioskPaymentId } = store.accountStore;
		const pp = getPPBehavior(ppType);
		const { sessionId } = store.sessionStore;
		const { kioskName } = store.kioskStore;

		// TEMPORARY FIX: Assuming single transaction for now
		const item = cartItems.length > 0 ? cartItems[0] : null;
		if (!item) {
			logger.logGeneral.error({}, 'No cart items found for receipt generation', 'no cart items');
			return null;
		}

		// Cash never carries a fee. transactionFee can still hold a card/check fee from a screen the
		// customer backed out of, so it must not be used for a cash receipt.
		// Otherwise use transactionFee from store (dynamically fetched) instead of item.feeAmount (static from payment methods).
		// For integrations that implement resolvePaymentFee (e.g. Fiserv), use it directly with the actual
		// paymentType so the fee matches the tender used without relying on transactionFee being set.
		const feeAmount =
			paymentType === PaymentType.Cash
				? 0
				: pp.resolvePaymentFee && paymentType !== undefined
					? pp.resolvePaymentFee(item, paymentType)
					: transactionFee
						? parseFloat(transactionFee)
						: item.feeAmount;
		const selectedAmountToPayWithFee = cartTotalAmount! + feeAmount;
		logger.logGeneral.warn({}, 'TEMPORARY FIX: Assuming single transaction for now');

		// Create header data
		const headerData: ReceiptHeaderData = {
			companyLogoPath: store.uiStore.printedLogoPath || logoPath,
			companyName: companyLabelValue,
			companyPhone: phoneLabelValue,
			extraInfo: this.mapStaticKvpList(headerStaticKvpList, getString),
		};

		// Create body data (single transaction entry)
		const bodyItem: ReceiptBodyData = {
			datetime: {
				label: getString(dateTimeLabelKey),
				value: formatLocalizedDateTime(new Date(), currentLanguage),
			},
			customerId: {
				label: getString(customerIdLabelKey),
				value: companyPolicy.obscuring.printCustomerId
					? (item.customer?.obscuredCustomerId ?? item.customer?.customerId ?? '')
					: (item.customer?.customerId ?? ''),
			},
			sessionNumber: { label: getString(sessionNumberLabelKey), value: sessionId! },
			amountPaid: { label: getString(amountPaidLabelKey), value: this.formatCurrency(cartTotalAmount!) },
			fee: { label: getString(feeLabelKey), value: this.formatCurrency(feeAmount) },
			total: { label: getString(totalAmountLabelKey), value: this.formatCurrency(selectedAmountToPayWithFee!) },
			kioskNameOrId: { label: getString(kioskNameLabelKey), value: kioskName },
			paymentType: {
				label: getString(paymentTypeLabelKey),
				value: getString(enumHelper.getPaymentTypeLabelKey(paymentType)),
			},
		};

		// Optional Invoice Number row — driven by the PP integration. Hidden when the integration
		// returns null (e.g. Fiserv, which does not surface an invoice number).
		const invoiceNumberValue = pp.getReceiptInvoiceNumber(item.item, cartId, companyPolicy.obscuring.printItemNumber);
		if (invoiceNumberValue) {
			bodyItem.invoiceNumber = {
				label: getString(invoiceNumberLabelKey),
				value: invoiceNumberValue,
			};
		}

		// Optional Payment Number row — driven by the PP integration. Hidden if the integration
		// returns null/undefined (e.g. non-Fiserv) or if no kioskPaymentId is available.
		const paymentNumberValue = pp.getReceiptPaymentNumber?.(kioskPaymentId);
		if (paymentNumberValue) {
			bodyItem.paymentNumber = {
				label: getString(paymentNumberLabelKey ?? 'payment-number'),
				value: paymentNumberValue,
			};
		}

		const amountDue = item.item.amountDue;
		if ((showPreviousBalance ?? false) && amountDue != null && amountDue !== 0) {
			bodyItem.previousBalance = {
				label: getString(previousBalanceLabelKey ?? 'previous-balance'),
				value: this.formatCurrency(amountDue),
			};
		}

		if (paymentType === PaymentType.Cash) {
			bodyItem.amountPaid.value = this.formatCurrency(item.amountPaid);
			bodyItem.total.value = this.formatCurrency(item.amountPaid + feeAmount);
		}

		bodyItem.approximateAccountBalance = {
			label: getString(approximateAccountBalanceLabelKey),
			value: this.formatCurrency(item.approximateAccountBalance),
		};

		// Create footer data
		const footerData: ReceiptFooterData = {
			bpkLogoPath: bpkLogo,
			barcodePath: barcode,
			extraInfo: this.mapStaticKvpList(footerStaticKvpList, getString),
		};

		return {
			headerData,
			items: [bodyItem], // Single transaction
			cashBoxItems: [],
			footerData,
		};
	};

	private mapStaticKvpList = (
		list: ReceiptStaticKvp[],
		getString: (key: string) => string,
	): TranslatedReceiptStaticKvp[] =>
		list
			.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
			.map(({ labelKey, value, order, blankLineAfter, translate }) => ({
				// Only translate lines explicitly flagged. Untranslated lines (e.g. a URL on its
				// own line) render verbatim so they're never altered by the dictionary lookup.
				label: translate ? getString(labelKey) : labelKey,
				value,
				order,
				blankLineAfter,
			}));

	private formatCurrency = (amount: number): string => formatCurrencyDisplay(amount);

	private generateDemoReceiptData = (): ReceiptData => {
		const { getString, currentLanguage } = store.configStore;
		const currentDate = formatLocalizedDateTime(new Date(), currentLanguage);

		const headerData: ReceiptHeaderData = {
			companyLogoPath: store.uiStore.printedLogoPath,
			companyName: 'Fort Bend County Tax Office',
			companyPhone: '(210) 828-8343',
		};

		const bodyItem: ReceiptBodyData = {
			datetime: { label: getString('Date/Time'), value: currentDate },
			customerId: { label: getString('Customer ID'), value: 'DEMO-CUSTOMER-123' },
			invoiceNumber: { label: getString('Invoice Number'), value: `R${Date.now().toString().slice(-8)}` },
			sessionNumber: { label: getString('Session Number'), value: `DEMO-${Date.now().toString().slice(-6)}` },
			amountPaid: { label: getString('Amount Paid'), value: this.formatCurrency(125.5) },
			fee: { label: getString('Fee'), value: this.formatCurrency(3.95) },
			total: { label: getString('Total'), value: this.formatCurrency(129.45) },
			approximateAccountBalance: {
				label: getString('Approximate Account Balance'),
				value: this.formatCurrency(1250),
			},
			kioskNameOrId: { label: getString('Kiosk Name/ID'), value: 'DEMO-KIOSK-001' },
			paymentType: { label: getString('Payment Type'), value: getString('credit-label') },
		};

		const footerData: ReceiptFooterData = {
			bpkLogoPath: bpkLogo,
			barcodePath: barcode,
			extraInfo: [
				{ order: 1, label: getString('Website'), value: 'www.billpay-kiosk.com' },
				{ order: 2, label: getString('Support'), value: 'Call (123) 456-7890' },
				{ order: 3, label: getString('Note'), value: 'Demo receipt for testing purposes only' },
			],
		};

		return {
			headerData,
			items: [bodyItem],
			cashBoxItems: [],
			footerData,
		};
	};

	private generaterateDeviceTestReceiptData = ({
		logoPath,
		companyLabelValue,
		phoneLabelValue,
		headerStaticKvpList,
		dateTimeLabelKey,
		customerIdLabelKey,
		invoiceNumberLabelKey,
		sessionNumberLabelKey,
		amountPaidLabelKey,
		feeLabelKey,
		totalAmountLabelKey,
		approximateAccountBalanceLabelKey,
		kioskNameLabelKey,
		paymentTypeLabelKey,
		footerStaticKvpList,
	}: ReceiptPrintServerProps): ReceiptData => {
		const { getString, currentLanguage } = store.configStore;
		const { kioskName } = store.kioskStore;

		const headerData: ReceiptHeaderData = {
			companyLogoPath: store.uiStore.printedLogoPath || logoPath,
			companyName: companyLabelValue,
			companyPhone: phoneLabelValue,
			extraInfo: this.mapStaticKvpList(headerStaticKvpList, getString),
		};

		const bodyItem: ReceiptBodyData = {
			datetime: {
				label: getString(dateTimeLabelKey),
				value: formatLocalizedDateTime(new Date(), currentLanguage),
			},
			customerId: { label: getString(customerIdLabelKey), value: 'TEST-CUSTOMER-123' },
			invoiceNumber: { label: getString(invoiceNumberLabelKey), value: `R${Date.now().toString().slice(-8)}` },
			sessionNumber: { label: getString(sessionNumberLabelKey), value: `TEST-${Date.now().toString().slice(-6)}` },
			amountPaid: { label: getString(amountPaidLabelKey), value: this.formatCurrency(125.5) },
			fee: { label: getString(feeLabelKey), value: this.formatCurrency(3.95) },
			total: { label: getString(totalAmountLabelKey), value: this.formatCurrency(129.45) },
			approximateAccountBalance: { label: getString(approximateAccountBalanceLabelKey), value: this.formatCurrency(1250) },
			kioskNameOrId: { label: getString(kioskNameLabelKey), value: kioskName },
			paymentType: {
				label: getString(paymentTypeLabelKey),
				value: getString(enumHelper.getPaymentTypeLabelKey(PaymentType.Card)),
			},
		};

		const footerData: ReceiptFooterData = {
			bpkLogoPath: bpkLogo,
			barcodePath: barcode,
			extraInfo: this.mapStaticKvpList(footerStaticKvpList, getString),
		};

		return {
			headerData,
			items: [bodyItem],
			cashBoxItems: [],
			footerData,
		};
	};

	private generateCashBoxTotalsReceiptData = (
		{
			logoPath,
			companyLabelValue,
			phoneLabelValue,
			headerStaticKvpList,
			dateTimeLabelKey,
			kioskNameLabelKey,
			footerStaticKvpList,
		}: ReceiptPrintServerProps,
		removalData?: CashBoxRemovalReceiptData
	): ReceiptData => {
		const { getString } = store.configStore;
		const { cashBoxTotals, authenticatedUser } = store.bpkManagerStore;
		const { kioskName } = store.kioskStore;

		if (!cashBoxTotals) throw new Error('No cash box totals available');

		const headerData: ReceiptHeaderData = {
			companyLogoPath: store.uiStore.printedLogoPath || logoPath,
			companyName: companyLabelValue,
			companyPhone: phoneLabelValue,
			extraInfo: this.mapStaticKvpList(headerStaticKvpList, getString),
		};

		const billCount =
			cashBoxTotals.ones +
			cashBoxTotals.twos +
			cashBoxTotals.fives +
			cashBoxTotals.tens +
			cashBoxTotals.twenties +
			cashBoxTotals.fifties +
			cashBoxTotals.hundreds;

		const cashAmount =
			cashBoxTotals.ones * 1 +
			cashBoxTotals.twos * 2 +
			cashBoxTotals.fives * 5 +
			cashBoxTotals.tens * 10 +
			cashBoxTotals.twenties * 20 +
			cashBoxTotals.fifties * 50 +
			cashBoxTotals.hundreds * 100;

		const bodyItem: ReceiptBodyCashBoxData = {
			datetime: {
				label: getString(dateTimeLabelKey),
				value: (removalData?.removalTime ?? new Date()).toLocaleString(),
			},
			kioskNameOrId: { label: getString(kioskNameLabelKey), value: kioskName },
			employeeName: {
				label: getString('Employee Name'),
				value: removalData?.employeeName ?? authenticatedUser?.displayName ?? '',
			},
			...cashBoxTotals,
			totalBills: billCount,
			totalCash: this.formatCurrency(cashAmount),
		};

		const footerData: ReceiptFooterData = {
			bpkLogoPath: bpkLogo,
			barcodePath: barcode,
			extraInfo: this.mapStaticKvpList(footerStaticKvpList, getString),
		};

		return {
			headerData,
			items: [],
			cashBoxItems: [bodyItem],
			footerData,
		};
	};
}
